import React from 'react';
import Icon from '../../../components/AppIcon';

const ServiceComparison = ({ services, onClose }) => {
  const comparisonCategories = [
    { key: 'description', label: 'Description' },
    { key: 'benefits', label: 'Key Benefits' },
    { key: 'technologies', label: 'Technologies' },
    { key: 'pricing', label: 'Pricing Range' },
    { key: 'timeline', label: 'Typical Timeline' }
  ];

  const getTimelineEstimate = (serviceId) => {
    const timelines = {
      'web-development': '4-12 weeks',
      'mobile-development': '8-20 weeks',
      'cloud-solutions': '2-8 weeks',
      'data-analytics': '6-16 weeks',
      'cybersecurity': '3-10 weeks',
      'ai-ml': '12-24 weeks'
    };
    return timelines[serviceId] || '4-16 weeks';
  };

  const formatPricing = (pricing) => {
    return `${pricing.basic} - ${pricing.premium}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-text-primary">
              Service Comparison
            </h2>
            <p className="text-text-secondary">
              Compare features and pricing across selected services
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary nav-transition focus-ring rounded-md"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Service Headers */}
            <div className="flex border-b border-border">
              <div className="w-48 p-4 bg-secondary-50 font-medium text-text-primary">
                Features
              </div>
              {services.map((service) => (
                <div key={service.id} className="flex-1 min-w-64 p-4 text-center border-l border-border">
                  <div className="flex items-center justify-center space-x-3 mb-2">
                    <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                      <Icon name={service.icon} size={20} color="var(--color-primary)" />
                    </div>
                    <h3 className="font-semibold text-text-primary">{service.title}</h3>
                  </div>
                  <p className="text-sm text-text-secondary">{service.shortDesc}</p>
                </div>
              ))}
            </div>

            {/* Comparison Rows */}
            <div className="max-h-[60vh] overflow-y-auto">
              {comparisonCategories.map((category, categoryIndex) => (
                <div key={category.key} className={`flex ${categoryIndex % 2 === 0 ? 'bg-secondary-50' : 'bg-surface'}`}>
                  <div className="w-48 p-4 font-medium text-text-primary border-b border-border">
                    {category.label}
                  </div>
                  {services.map((service) => (
                    <div key={service.id} className="flex-1 min-w-64 p-4 border-l border-b border-border">
                      {category.key === 'description' && (
                        <p className="text-sm text-text-secondary leading-relaxed">
                          {service.description}
                        </p>
                      )}
                      
                      {category.key === 'benefits' && (
                        <div className="space-y-2">
                          {service.benefits.slice(0, 4).map((benefit, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <Icon name="Check" size={14} color="var(--color-success)" />
                              <span className="text-sm text-text-secondary">{benefit}</span>
                            </div>
                          ))}
                          {service.benefits.length > 4 && (
                            <div className="text-xs text-text-secondary">
                              +{service.benefits.length - 4} more
                            </div>
                          )}
                        </div>
                      )}
                      
                      {category.key === 'technologies' && (
                        <div className="flex flex-wrap gap-1">
                          {service.technologies.map((tech, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-primary-50 text-primary rounded text-xs"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {category.key === 'pricing' && (
                        <div>
                          <div className="text-lg font-semibold text-primary mb-1">
                            {formatPricing(service.pricing)}
                          </div>
                          <div className="text-xs text-text-secondary">
                            Starting from {service.pricing.basic}
                          </div>
                        </div>
                      )}
                      
                      {category.key === 'timeline' && (
                        <div className="flex items-center space-x-2">
                          <Icon name="Clock" size={16} color="var(--color-accent)" />
                          <span className="text-sm text-text-secondary">
                            {getTimelineEstimate(service.id)}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border bg-secondary-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text-secondary">
              Need help choosing? Contact us for a personalized consultation.
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                Close Comparison
              </button>
              <button
                onClick={() => {
                  // Scroll to contact section
                  onClose();
                  setTimeout(() => {
                    const contactSection = document.getElementById('contact');
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
                className="btn-primary"
              >
                Get Quote for All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceComparison;